import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { productConfig } from '../config/product';
import '../features/auth/login.css';

function isValidEmail(email: string): boolean {
  return /^\S+@\S+\.\S+$/.test(email.trim());
}

export function LoginPage() {
  const { requestMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | undefined>();
  const sentHeadingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the confirmation heading so keyboard/SR users aren't stranded
  // when the form is swapped for the "Check your email" state.
  useEffect(() => {
    if (sent) sentHeadingRef.current?.focus();
  }, [sent]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('Enter your work email address.');
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(undefined);

    setLoading(true);
    try {
      const { devLink: link } = await requestMagicLink(trimmed);
      setDevLink(link);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send sign-in link.');
    } finally {
      setLoading(false);
    }
  }

  const emailErrorId = emailError ? 'login-email-error' : undefined;
  const formErrorId = error ? 'login-form-error' : undefined;

  return (
    <main className="stage bg-grid login-stage" data-screen-label="Sign in">
      <div className="glow bg-glow g1" aria-hidden="true" />
      <div className="glow bg-glow g2" aria-hidden="true" />

      <div className="wrap">
        <section className="signin-card" aria-labelledby="login-heading">
          <div className="brand" aria-label={productConfig.name}>
            <svg className="mk" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <circle
                cx="16"
                cy="16"
                r="11"
                stroke="currentColor"
                strokeOpacity="0.35"
                strokeWidth="1.5"
              />
              <path
                d="M16 5a11 11 0 0 1 9.5 5.5"
                stroke="var(--login-accent)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="16" cy="16" r="3.4" fill="var(--login-accent)" />
              <circle cx="25.5" cy="10.5" r="2" fill="var(--login-accent)" />
            </svg>
            <span className="nm">
              RegenLoop<span className="ai">.ai</span>
            </span>
          </div>

          {sent ? (
            <>
              <h1 id="login-heading" ref={sentHeadingRef} tabIndex={-1}>
                Check your email
              </h1>
              <p className="sub">
                If an account exists for <strong>{email.trim()}</strong>, we&apos;ve sent a sign-in
                link. It expires in 15 minutes and can be used once.
              </p>
              {devLink && (
                <div className="form-error dev-link" role="status">
                  Dev link (email not sent locally): <a href={devLink}>{devLink}</a>
                </div>
              )}
              <button
                type="button"
                className="submit"
                onClick={() => {
                  setSent(false);
                  setDevLink(undefined);
                }}
              >
                <span className="lbl">Use a different email</span>
              </button>
            </>
          ) : (
            <>
              <h1 id="login-heading">Sign in</h1>
              <p className="sub">
                Enter your work email and we&apos;ll send you a secure sign-in link.
              </p>

              <form
                onSubmit={(e) => void handleSubmit(e)}
                noValidate
                aria-describedby={formErrorId}
              >
                <div className={`field${emailError ? ' field-invalid' : ''}`}>
                  <label htmlFor="login-email">Work email</label>
                  <div className="inwrap">
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError(undefined);
                      }}
                      placeholder="name@institution.org"
                      autoComplete="email"
                      inputMode="email"
                      required
                      aria-invalid={Boolean(emailError)}
                      aria-describedby={emailErrorId}
                      disabled={loading}
                    />
                  </div>
                  {emailError && (
                    <span className="error-text" id="login-email-error" role="alert">
                      {emailError}
                    </span>
                  )}
                </div>

                {error && (
                  <div className="form-error" id="login-form-error" role="alert">
                    {error}
                  </div>
                )}

                <button
                  className={`submit${loading ? ' loading' : ''}`}
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                >
                  <span className="lbl">Email me a sign-in link</span>
                  <svg className="arr" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                  <svg className="sp" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 3a9 9 0 1 0 9 9" />
                  </svg>
                </button>
              </form>
            </>
          )}
        </section>

        <footer className="foot">
          <span>© 2026 RegenLoop.ai</span>
          <a href="/privacy">Privacy</a>
          <a href="/security">Security</a>
          <a href="/status">Status</a>
        </footer>
      </div>
    </main>
  );
}
